import { linkFiles, useUpload } from "@/api/files";
import { recordingOwner } from "@/components/wiki/recording-owner";
import { WikiPageForm } from "@/forms/wiki-page-form";
import { useAuth } from "@/hooks/use-auth";
import { useLeaveGuard } from "@/hooks/use-leave-guard";
import type { useWikiPage } from "@/hooks/use-wiki-page";
import { UploadMediaModal } from "@/modals/wiki/upload-media-modal";
import { client } from "@/hono";
import { Button, ButtonGroup } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { type Editor, insertBlock } from "@msuite/ui/libs/tiptap";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { useVideoRecorder, VideoRecorderStartModal } from "@msuite/ui/video-recorder";
import { type WikiPageSchema, wikiPageSchema } from "@msuite/utils/schemas";
import React, { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { prepareRecording } from "./prepare-recording";
import { WikiPageByline } from "./wiki-page-byline";
import { WikiPageTrail } from "./wiki-page-trail";

/**
 * A page's stored content as a Tiptap document, or null when the page holds no document — a page
 * created and not yet saved carries an empty string, and older pages carry plain text.
 */
function parseEditorContent(content: string) {
	try {
		const document = JSON.parse(content);
		return document && typeof document === "object" ? document : null;
	} catch {
		return null;
	}
}

/** The page row as the page's own query returns it: the editor edits what the reader saw. */
type WikiPageRow = NonNullable<ReturnType<typeof useWikiPage>["wikiPage"]>;

/** Props Interface */
interface WikiPageEditorProps {
	page: WikiPageRow;
	spaceId: string;
	pageId: string;
	isCreate?: boolean;
	onClose: () => void;
}

export function WikiPageEditor({
	page,
	spaceId,
	pageId,
	isCreate = false,
	onClose,
}: WikiPageEditorProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization, user } = useAuth();
	const navigate = useNavigate();
	const qc = useQueryClient();
	const dismissModal = useAsyncModal();
	const uploadModal = useDisclosure();
	const { uploadFn } = useUpload();
	const methods = useForm<WikiPageSchema>({
		resolver: resolver(wikiPageSchema),
		// The row the page already holds, not a fetch of its own: the form is ready on its first
		// render, so the editor appears in the frame the reading view leaves. A fetch here put a
		// loading state between the two, and the switch blinked.
		defaultValues: {
			title: page.title ?? "",
			content: page.content,
			spaceId: page.spaceId,
			parentPageId: page.parentPageId,
		},
	});
	// Unsaved edits hold the page: leaving through a link, the sidebar or the back button asks
	// first, and a reload gets the browser's own prompt.
	const leaveGuard = useLeaveGuard({
		isDirty: methods.formState.isDirty,
		confirmLeave: () => dismissModal.confirm(),
	});

	/** Store */
	const recorder = useVideoRecorder();

	/** State */
	const editorRef = useRef<Editor | null>(null);
	// The header slot the toolbar renders into. State rather than a ref, because the form has to
	// render again once the slot exists — a ref changing tells nobody.
	const [menuSlot, setMenuSlot] = useState<HTMLDivElement | null>(null);

	/** Functions */
	async function handleOnSubmit(json: WikiPageSchema) {
		const response = await client.wiki.spaces[":spaceId"].pages[":pageId"].$patch({
			param: { spaceId, pageId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-page", pageId]) });
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces", spaceId, "pages"]) });
		// What was saved is what the form holds, so nothing is left to guard on the way out.
		methods.reset(json);
		leaveGuard.release();
		navigate(`/wiki/${spaceId}/${pageId}`, { replace: true });
		onClose();
	}

	async function handleOnDismiss() {
		if (methods.formState.isDirty) {
			const isConfirmed = await dismissModal.confirm();
			if (!isConfirmed) return;
		}
		const response = await client.wiki.spaces[":spaceId"].pages[":pageId"].$delete({
			param: { spaceId, pageId },
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["wiki-spaces", spaceId, "pages"]) });
		leaveGuard.release();
		navigate(`/wiki/${spaceId}`, { replace: true });
	}

	/** Closes the editor, asking first when there is something to lose. */
	async function handleOnCancel() {
		if (methods.formState.isDirty && !(await dismissModal.confirm())) return;
		leaveGuard.release();
		onClose();
	}

	/**
	 * Keeps the editor for the callers below, and takes its reading of the document as the form's
	 * baseline. The stored string and the editor's own serialisation of the same document differ
	 * in form alone — whitespace, key order, an attribute the editor fills in — and the form compares
	 * strings, so without this the first keystroke made a page dirty for good, and a character typed
	 * and deleted again still raised the warning on the way out. An empty document is written as an
	 * empty string, the way the editor reports it.
	 */
	function handleOnEditorReady(editor: Editor | null) {
		editorRef.current = editor;
		if (!editor) return;
		const baseline = editor.isEmpty ? "" : JSON.stringify(editor.getJSON());
		methods.resetField("content", { defaultValue: baseline });
	}

	/**
	 * Writes the placeholder block at the cursor and pushes the result straight into the form.
	 * Reading it out of the editor rather than waiting for its change event keeps the save below
	 * from racing a React update and persisting the page without the block.
	 */
	function insertVideoBlock(videoId: string) {
		const editor = editorRef.current;
		if (!editor) return false;
		insertBlock(editor, { type: "videoBlock", attrs: { videoId } });
		methods.setValue("content", JSON.stringify(editor.getJSON()), { shouldDirty: true });
		return true;
	}

	/**
	 * Writes the current draft to the server and reports whether it landed. The error toast is
	 * suppressed because both callers say something better: one names the recording it just
	 * called off, the other is a rollback the author never asked for and should not be told about.
	 */
	async function savePage() {
		try {
			const response = await client.wiki.spaces[":spaceId"].pages[":pageId"].$patch({
				param: { spaceId, pageId },
				json: methods.getValues(),
			});
			await responseHandler(response, false);
			await qc.invalidateQueries({ predicate: invalidate(["wiki-page", pageId]) });
			// The server now holds what the form holds; dirty tracking starts over from here.
			methods.reset(methods.getValues());
			return true;
		} catch (error) {
			console.error("Saving the page failed:", error);
			return false;
		}
	}

	/** Saves the page and reports whether it worked, so a failed save can call the recording off. */
	async function saveBeforeRecording() {
		const isSaved = await savePage();
		// The placeholder is already in the page by now, so silence here reads as the capture
		// refusing to start. It is the page that refused.
		if (!isSaved) toast.error(t("wiki.save_before_recording_failed"));
		return isSaved;
	}

	/**
	 * Puts the page back the way it was when a recording never came of it.
	 *
	 * The placeholder is written and saved before the capture is confirmed, so an attempt that
	 * ends there — a dismissed picker, a refused microphone — would otherwise leave a block on
	 * the page that spins forever, and whose row the nightly sweep reaps a day later, turning the
	 * spinner into "this video no longer exists" with nothing but a manual edit to cure it.
	 */
	async function rollbackVideoBlock(content: string) {
		const editor = editorRef.current;
		// A page that has never been saved holds an empty string where a document belongs, and
		// parsing it throws. That throw used to escape the whole attempt: it reported a capture
		// that had failed to start, and it skipped the cleanup behind it, leaving a video row
		// with no block anywhere to reach it.
		const document = parseEditorContent(content);
		if (document) editor?.commands.setContent(document);
		else editor?.commands.clearContent();
		methods.setValue("content", content, { shouldDirty: true });
		await savePage();
	}

	/**
	 * Opens the source picker, but only for a page the server would accept.
	 *
	 * Recording anchors a placeholder in the page and saves it, which is what lets the author
	 * walk away and keep recording — so a page missing a required field has nowhere to put the
	 * recording. Checking here marks the field that is missing; the attempt otherwise died after
	 * the surface picker, where the only thing left to report was that the recording had not
	 * started.
	 */
	async function handleOnOpenRecorder() {
		if (!(await methods.trigger())) {
			toast.error(t("wiki.record_needs_saveable_page"));
			return;
		}
		recorder.disclosure.onOpen();
	}

	async function handleOnPrepareRecording() {
		const contentBefore = methods.getValues("content");
		const target = await prepareRecording({
			spaceId,
			pageId,
			pageTitle: methods.getValues("title"),
			owner: recordingOwner(organization.id ?? "", user?.id ?? ""),
			insertBlock: insertVideoBlock,
			save: saveBeforeRecording,
			rollback: () => rollbackVideoBlock(contentBefore),
		});
		return target;
	}

	/**
	 * Writes an image block at the cursor and pushes the result straight into the form, the way
	 * the video block is written.
	 */
	function insertImageBlock(fileId: string) {
		const editor = editorRef.current;
		if (!editor) return false;
		insertBlock(editor, { type: "imageBlock", attrs: { fileId } });
		methods.setValue("content", JSON.stringify(editor.getJSON()), { shouldDirty: true });
		return true;
	}

	/** Opens the upload dialog, under the same condition as the recorder: a page the server would accept. */
	async function handleOnOpenUpload() {
		if (!(await methods.trigger())) {
			toast.error(t("wiki.record_needs_saveable_page"));
			return;
		}
		uploadModal.onOpen();
	}

	/**
	 * Puts a chosen file into the page, and saves the page around it the way a recording does.
	 *
	 * An image is uploaded through the files API, linked to the page as one of its files, written
	 * into the text as a block, and the page saved — so that the block always names a file the page
	 * holds. A video takes the recording's own path: a row, a block anchored by a save, and the file
	 * streamed to Mux from here, so it turns into a player on its own even if the author leaves.
	 * Either way the page is saved once the block is in, which is what lets the upload outlive the
	 * editor; it is the same trade the recorder makes.
	 */
	async function handleOnUploadMedia(file: File, onProgress: (fraction: number) => void) {
		if (file.type.startsWith("image/")) {
			const body = new FormData();
			body.append("files", file);
			const result = (await uploadFn(body))?.[0];
			if (!result) throw new Error("videos.insert_image_failed");
			await linkFiles("wikiPageFiles", { wikiPageId: pageId }, { uploadResults: [result] });
			if (!insertImageBlock(result.id) || !(await savePage())) {
				throw new Error("videos.insert_image_failed");
			}
			onProgress(1);
			return;
		}

		const contentBefore = methods.getValues("content");
		const target = await prepareRecording({
			spaceId,
			pageId,
			pageTitle: methods.getValues("title"),
			owner: recordingOwner(organization.id ?? "", user?.id ?? ""),
			insertBlock: insertVideoBlock,
			save: savePage,
			rollback: () => rollbackVideoBlock(contentBefore),
		});
		if (!target) throw new Error("videos.insert_video_failed");
		try {
			await target.upload(file, onProgress);
		} catch (error) {
			// A file that never reached Mux leaves a block spinning for nothing; the row and the block
			// go together, the way a capture that never started takes its placeholder with it.
			await target.abandon?.();
			throw error;
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<FormProvider {...methods}>
				<form
					className="flex min-h-0 flex-1 flex-col"
					onSubmit={methods.handleSubmit(handleOnSubmit)}
				>
					{/* The same row the reading view has, with the formatting toolbar where the
					    page's actions were: top right is what one can do to the page, in either
					    state. The toolbar wraps before the trail truncates. The negative margins
					    carry the row out to the column's padding, so the rule under it spans the
					    column. */}
					<div className="flex min-h-8 flex-row items-center justify-between gap-4 -mx-4 -mt-4 p-4 border-b border-color">
						<WikiPageTrail
							spaceId={spaceId}
							pageId={pageId}
						/>
						<div
							ref={setMenuSlot}
							className="flex min-w-0 justify-end"
						/>
					</div>
					{/* Everything between the two rows is one block that takes the height they
					    leave, which is what keeps them pinned. The text box scrolls inside itself
					    from there — `size="full"` on it, not an overflow here. */}
					<div className="flex min-h-0 flex-1 flex-col">
						<WikiPageForm
							byline={
								<WikiPageByline
									page={page}
									className="mt-2.5"
								/>
							}
							menuContainer={menuSlot}
							onEditorReady={handleOnEditorReady}
						/>
					</div>
					{/* The form's own buttons close the column. Recording and uploading stand together
					    on the left: they act on the document, not on the form. Cancelling and saving
					    stand together on the right, where a form ends. */}
					<div className="flex min-h-8 shrink-0 flex-row items-center justify-between gap-4 -mx-4 -mb-4 p-4 border-t border-color">
						<ButtonGroup isAttached={false}>
							<Button
								variant="ghost"
								type="button"
								onClick={handleOnOpenRecorder}
								// The recorder takes one recording at a time, and it holds on to a
								// take whose upload failed until the author retries or discards it.
								// Without this the button simply did nothing, with the floating
								// panel in the corner as the only hint why.
								isDisabled={!recorder.canStart}
								icon={<Icon name="Video" />}
							>
								{t("video_recorder.capture_video")}
							</Button>
							<Button
								variant="ghost"
								type="button"
								onClick={handleOnOpenUpload}
								icon={<Icon name="Upload" />}
							>
								{t("videos.upload_media")}
							</Button>
						</ButtonGroup>
						<ButtonGroup isAttached={false}>
							{isCreate ? (
								<Button
									variant="danger-ghost"
									type="button"
									onClick={handleOnDismiss}
								>
									{t("general.dismiss")}
								</Button>
							) : (
								<Button
									variant="ghost"
									type="button"
									onClick={handleOnCancel}
								>
									{t("general.cancel")}
								</Button>
							)}
							<Button
								variant="primary"
								type="submit"
								isLoading={methods.formState.isSubmitting}
							>
								{t("general.save")}
							</Button>
						</ButtonGroup>
					</div>
				</form>
			</FormProvider>
			<ConfirmModal
				controller={dismissModal}
				title={t("general.dismiss")}
				confirmLabel={t("general.dismiss")}
				message={t("general.dismiss_unsaved_changes")}
			/>
			{uploadModal.isOpen && (
				<UploadMediaModal
					isOpen={uploadModal.isOpen}
					onClose={uploadModal.onClose}
					onUpload={handleOnUploadMedia}
				/>
			)}
			{recorder.disclosure.isOpen && (
				<VideoRecorderStartModal
					onPrepare={handleOnPrepareRecording}
					// Only once a capture is genuinely running does the editor step aside. Closing
					// it earlier took this modal down with it, so a failed start had nowhere left
					// to report itself.
					onStarted={onClose}
				/>
			)}
		</React.Fragment>
	);
}
